import React, { useEffect, useMemo, useRef, useState } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { Grid, TextField } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { RootStore } from '../../redux/store'
import Card from '../../shared/layout/Card'
import Paragraph from '../../shared/UI/Paragraph'
import {
  hideModal,
  SetShowModalPayload,
  showModalDialog,
} from '../../redux/reducers/appSettingsReducer'
import Modal from '../../shared/UI/Modal'
import InfoBanner from '../../shared/UI/InfoBanner'
import { getLocalDateString } from '../../utils/date-utils'
import { AbsenceDetailProps } from './types'
import Row from './Row'
import DropdownMenu from '../../shared/UI/DropdownMenu'
import { getSelectValuesByType } from '../../utils/app-utils'
import { manualRequestsService } from '../../services/myActionsService'
import { AbsenceDay } from '../../types/absence-day'
import LoadingIndicator from '../../shared/UI/LoadingIndicator'
import NoDataFound from '../../shared/UI/NoDataFound'
import { calcEmptyTableCell, deleteAbsenceDay, isFormValid } from './functions'
import { absenceFormLabels } from '../../utils/constants'

export default function AbsenceDetail({
  absenceDays,
  absenceId,
  onEdit,
  onDelete,
  handleUpdate,
  isLoading,
  isData,
  viewOnly = false,
  handleViewClick,
  isPayrollApproverReadOnlyView = false,
  hasFutureSicknessAbsence = false,
  isRtwComplete = false,
  rtwRequired = true,
  absenceStatusType
}: AbsenceDetailProps) {
  const [selectedRow, setSelectedRow] = useState<AbsenceDay | null>(null)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleteComment, setDeleteComment] = useState('')
  const [absenceReasonType, setAbsenceReasonType] = useState<string>()
  const [location, setLocation] = useState<boolean>(false)
  const [validationErrors, setValidationErrors] = useState<any>({})

  const isDisabled = useMemo(
    () => !(deleteReason && deleteComment) || hasFutureSicknessAbsence,
    [deleteReason, deleteComment, hasFutureSicknessAbsence]
  )

  const dispatch = useDispatch()
  const { showModal, title, message, type, buttonLabel } = useSelector<
    RootStore,
    SetShowModalPayload
  >((state: RootStore) => state.appSettings.modalProps)

  const handleDeleteModal = (row: AbsenceDay) => {
    setSelectedRow(row)
  }

  const routerLocation = useLocation()
  const returnTo = (routerLocation.state as { previousLocation?: string } | null)?.previousLocation || '/myactions'

  const { absenceType } = absenceDays[0]

  const validateForm = () => isFormValid(setValidationErrors, deleteReason, deleteComment)

  useEffect(() => {
    setAbsenceReasonType(absenceType.reasonType)
  }, [absenceType])

  useEffect(() => {
    if (window.location.pathname.includes('myavailability')) {
      setLocation(false)
    } else {
      setLocation(true)
    }
  }, [absenceReasonType])

  const navigate = useNavigate()

  const didMountRef = useRef(false)

  useEffect(() => {
    if (didMountRef.current && selectedRow) {
      dispatch(
        showModalDialog({
          title: 'Are you sure?',
          message: `Are you sure you would like to delete this absence ${
            absenceDays.length > 1 ? 'day' : 'occurrence'
          } for the ${selectedRow && getLocalDateString(selectedRow.date)}?`,
          buttonLabel: 'Delete',
          type: 'question',
          showModal: true,
          isDisabled,
        })
      )
    }

    didMountRef.current = true
  }, [selectedRow])

  const submitRequest = () =>
    deleteAbsenceDay(
      selectedRow,
      absenceId,
      deleteReason,
      deleteComment,
      absenceDays,
      manualRequestsService,
      dispatch,
      onDelete,
      setDeleteComment,
      navigate,
      returnTo
    )

  const isSickness = useMemo(
    () => absenceType.reasonType?.toLowerCase() === 'sickness',
    [absenceType.reasonType]
  )

  return (
    <>
      <Grid container>
        <Modal
          type={type}
          open={showModal}
          onClose={() => {
            setSelectedRow(null)
            dispatch(hideModal())
          }}
          onClick={() => {
            if (validateForm()) {
              submitRequest()
            }
          }}
          title={title}
          message={message}
          buttonLabel={buttonLabel}
          isDisabled={isDisabled}
          maxWidth="xl"
          banner={
            hasFutureSicknessAbsence && isSickness ? (
              <InfoBanner
                dataTestId="future-sickness-warning-modal"
                margins={{ left: '0', right: '0', top: '16px', bottom: '0' }}
                text={absenceFormLabels.deleteAbsenceDayFutureSicknessAbsenceWarning}
                sx={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />
            ) : undefined
          }
          dropdown={
            <Grid container>
              <Grid item xs={12} md={12} spacing={4} mt={4}>
                <DropdownMenu
                  label="Reason"
                  id="reason"
                  data={getSelectValuesByType('AbsenceDeletionReason')}
                  textField="displayValue"
                  valueField="value"
                  onChange={e => {
                    const reasonStr = getSelectValuesByType('AbsenceDeletionReason').find(
                      x => x.value === Number(e.target.value)
                    )?.displayValue
                    setDeleteReason(reasonStr!)
                  }}
                  error={validationErrors.deleteReason}
                />
                <Grid mt={2}>
                  <TextField
                    label="Comments"
                    value={deleteComment}
                    fullWidth
                    multiline
                    rows={4}
                    onChange={e => {
                      setDeleteComment(e.target.value)
                    }}
                    error={validationErrors.deleteComment}
                  />
                </Grid>
              </Grid>
            </Grid>
          }
        />
      </Grid>
      <Card title="Absence Detail">
        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                {calcEmptyTableCell(location, absenceReasonType) ? <TableCell /> : null}
                <TableCell align="left">
                  <Paragraph weight="bold">Date</Paragraph>
                </TableCell>
                <TableCell align="left">
                  <Paragraph weight="bold">Hours</Paragraph>
                </TableCell>
                {isSickness && (
                  <TableCell align="left">
                    <Paragraph weight="bold">Sick Note Added</Paragraph>
                  </TableCell>
                )}
                <TableCell align="left">
                  <Paragraph weight="bold">Absence Type</Paragraph>
                </TableCell>
                {isSickness && (
                  <>
                    <TableCell align="left">
                      <Paragraph weight="bold">Sick Pay Available</Paragraph>
                    </TableCell>
                    <TableCell align="left">
                      <Paragraph weight="bold">Sick Pay Taken</Paragraph>
                    </TableCell>
                  </>
                )}
                <TableCell align="left">
                  <Paragraph weight="bold">To Work Back</Paragraph>
                </TableCell>
                <TableCell align="left">&nbsp;</TableCell>
                <TableCell align="left">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            {!isLoading && isData && (
              <TableBody>
                {absenceDays &&
                  absenceDays.map(row => (
                    <Row
                      key={row.id}
                      row={row}
                      onEdit={onEdit}
                      onDelete={handleDeleteModal}
                      handleUpdate={handleUpdate}
                      viewOnly={viewOnly}
                      handleViewClick={handleViewClick}
                      isSickness={isSickness}
                      isPayrollApproverReadOnlyView={isPayrollApproverReadOnlyView}
                      isRtwComplete={isRtwComplete}
                      rtwRequired={rtwRequired}
                      absenceStatusType={absenceStatusType}
                    />
                  ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        {!isLoading ? (
          <>
            {!isData && (
              <Grid xs={12} mt={4}>
                <NoDataFound show />
              </Grid>
            )}
          </>
        ) : (
          <Grid xs={12} mt={4}>
            <LoadingIndicator show />
          </Grid>
        )}
      </Card>
    </>
  )
}
